/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React from 'react';
import { trackFooterNavMenu } from '../utils/track';
import NextImage from '@/components/NextImage';
import { useLabels } from '@/hooks/useLabels';
import styles from './FooterIcon.module.scss';
import cx from 'classnames';

const labelMap = {
  newWindow: 'label_OpenaNewWindow',
};

type Props = {
  name: string;
  imageUrl?: string;
  altText?: string;
  className?: string;
  CTALink: string;
  footerSectionName?: 'top' | 'middle' | 'bottom' | '';
  aTagStyle?: string;
  width?: number;
  height?: number;
};

export function FooterIcon({
  name,
  imageUrl,
  altText,
  className,
  CTALink,
  footerSectionName = '',
  aTagStyle = '',
  width,
  height,
}: Props) {
  const labels = useLabels(labelMap);

  const altTextLowerCase = altText?.toLowerCase();

  const altTextMap: Record<string, string> = {
    facebook: 'Visit AutoZone on Facebook',
    twitter: 'Visit AutoZone on Twitter',
    x: 'Visit AutoZone on X (formerly Twitter)',
    instagram: 'Visit AutoZone on Instagram',
    youtube: 'Visit AutoZone on YouTube',
    linkedin: 'Visit AutoZone on LinkedIn',
    tiktok: 'Visit AutoZone on TikTok',
    pinterest: 'Visit AutoZone on Pinterest',
    digicert: 'Digicert trusted secure',
    appstore: 'Download on the App Store',
    googlestore: 'Get it on Google Play',
  };

  const descriptiveAltText =
    (altTextLowerCase && altTextMap[altTextLowerCase]) ??
    altText ??
    (name ? `Visit AutoZone on ${name}` : '');

  const imageComponent = (
    <div className={className}>
      {imageUrl ? (
        <NextImage
          src={imageUrl}
          alt={descriptiveAltText}
          data-testid={`footer_${footerSectionName}_icon_image_${name
            .split(' ')[0]
            ?.toLowerCase()}`}
          height={height}
          width={width}
          loading="lazy"
        />
      ) : null}
    </div>
  );
  return CTALink ? (
    <a
      href={CTALink}
      rel="noopener noreferrer"
      target="_blank"
      className={cx(styles.footerIconLink, aTagStyle)}
      onClick={() => trackFooterNavMenu(name)}
      data-testid={`footer_${footerSectionName}_icon_a_${name.split(' ')[0]?.toLowerCase()}`}
    >
      {imageComponent}
      <span className="sr-only">{labels.newWindow}</span>
    </a>
  ) : (
    imageComponent
  );
}
